/**
 * The route contract between features and the app router. Each feature's routes.tsx exports
 * `routes: AppRoute[]` with absolute paths; src/app/router.tsx mounts them all inside the
 * authenticated layout. See docs/frontend-foundation.md.
 */
import type { RouteObject } from "react-router";

export interface RouteHandle {
  /** Page title: the browser tab ("Materials – Aldehyde") and the command palette label. */
  title?: string;
  /** Extra words that find this page in the command palette ("lots", "stock"). */
  keywords?: string[];
  /** Keep a page out of the palette. Paths with parameters (":id") are always left out. */
  hideFromPalette?: boolean;
}

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** A react-router route object whose `handle` is typed as RouteHandle. */
export type AppRoute = DistributiveOmit<RouteObject, "handle" | "children"> & {
  handle?: RouteHandle;
  children?: AppRoute[];
};

export interface PageEntry {
  path: string;
  title: string;
  keywords: string[];
}

function joinPaths(parent: string, child: string | undefined): string {
  if (!child) return parent || "/";
  if (child.startsWith("/")) return child;
  return `${parent.replace(/\/$/, "")}/${child}`;
}

/** Every static page with a title, for navigation commands. */
export function collectPages(routes: readonly AppRoute[], parentPath = ""): PageEntry[] {
  const pages: PageEntry[] = [];
  for (const route of routes) {
    const path = route.index ? parentPath || "/" : joinPaths(parentPath, route.path);
    const title = route.handle?.title;
    const isStatic = !path.includes(":") && !path.includes("*");
    if (title && isStatic && !route.handle?.hideFromPalette && (route.index || route.path !== undefined)) {
      pages.push({ path, title, keywords: route.handle?.keywords ?? [] });
    }
    if (route.children) pages.push(...collectPages(route.children, path));
  }
  return pages;
}
